// Brand context provider for dynamic brand management
import React, { createContext, useContext, useState, useEffect } from 'react';

interface Brand {
  id: string;
  name: string;
  brand_id: string;
  industry?: string;
  logo_url?: string;
}

interface BrandContextType {
  currentBrandId: string;
  setBrandId: (brandId: string) => void;
  availableBrands: Brand[];
  loadBrands: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const BrandContext = createContext<BrandContextType | null>(null);

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentBrandId, setCurrentBrandId] = useState<string>('');
  const [availableBrands, setAvailableBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load brands on startup
  useEffect(() => {
    initializeBrands();
  }, []);

  const initializeBrands = async () => {
    try {
      setIsLoading(true);
      
      // First get authenticated user's brand_id from HTTP-Only cookies
      const API_BASE = process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app';
      
      let authenticatedBrandId = null;
      try {
        const authResponse = await fetch(`${API_BASE}/api/v1/auth/me`, {
          credentials: 'include'
        });
        if (authResponse.ok) {
          const userData = await authResponse.json();
          authenticatedBrandId = userData.brand_id;
          console.log('✅ BrandContext: Got authenticated brand_id:', authenticatedBrandId);
        }
      } catch (authError) {
        console.warn('⚠️ BrandContext: Failed to get authenticated brand_id:', authError);
      }
      
      // Load available brands from API
      let response;
      try {
        response = await fetch(`${API_BASE}/api/v1/brands/list`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
      } catch {
        response = await fetch(`${API_BASE}/api/v1/brands`, {
          credentials: 'include'
        });
      }
      
      if (response.status === 401 || response.status === 403) {
        // Not authenticated — this is expected on public/admin pages. Fail silently.
        setIsLoading(false);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        const brands = data.brands || data;
        setAvailableBrands(brands);
        
        // Use authenticated brand_id if available
        if (authenticatedBrandId) {
          setCurrentBrandId(authenticatedBrandId);
          console.log('✅ BrandContext: Set brand to authenticated user:', authenticatedBrandId);
        } else {
          // Smart brand selection: Look for kuniya brand first, then fallback to first available
          if (brands && brands.length > 0) {
            // Try to find kuniya brand first
            const kuniyaBrand = brands.find(brand => 
              (brand.name && brand.name.toLowerCase().includes('kuniya')) ||
              (brand.brand_name && brand.brand_name.toLowerCase().includes('kuniya')) ||
              (brand.contact_email && brand.contact_email.toLowerCase().includes('kuniya'))
            );
            
            const selectedBrand = kuniyaBrand || brands[0];
            const brandId = selectedBrand.brand_id || selectedBrand.id;
            setBrandId(brandId);
            
            if (kuniyaBrand) {
              console.log('🎯 BrandContext: Found and selected kuniya brand:', brandId, `(${selectedBrand.name || selectedBrand.brand_name})`);
              console.log('✅ FINAL SELECTION: Using KUNIYA brand for authentication!');
            } else {
              console.log('✅ BrandContext: Using first available brand:', brandId, `(${selectedBrand.name || selectedBrand.brand_name})`);
              console.log('ℹ️  FINAL SELECTION: Using first brand (no kuniya found)');
            }
          } else {
            console.error('❌ BrandContext: No brands available from API');
            setCurrentBrandId('test_brand'); // Emergency only
          }
        }
      } else {
        throw new Error(`Failed to load brands: ${response.status}`);
      }
    } catch (error) {
      // Silently fall back — unauthenticated pages don't need brand context
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app';
        const authResponse = await fetch(`${API_BASE}/api/v1/auth/me`, {
          credentials: 'include'
        });
        if (authResponse.ok) {
          const userData = await authResponse.json();
          setCurrentBrandId(userData.brand_id);
        }
      } catch {
        // No auth available — leave brand context empty
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadBrands = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const API_BASE = process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app';
      
      // Try multiple endpoints for brand loading
      let response;
      try {
        response = await fetch(`${API_BASE}/api/v1/brands`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
      } catch {
        // Fallback to emergency endpoint
        response = await fetch(`${API_BASE}/api/v1/emergency/brands/list`);
      }
      
      if (response.ok) {
        const data = await response.json();
        const brands = data.brands || data; // Handle different response formats
        setAvailableBrands(brands);
        
        // If no current brand selected, select the first available
        if (!currentBrandId && brands.length > 0) {
          setBrandId(brands[0].brand_id);
        }
      } else {
        throw new Error(`Failed to load brands: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to load brands:', error);
      setError('Failed to load brands. Using fallback.');
      
      // Fallback to known working brand
      const fallbackBrand: Brand = {
        id: 'fallback-id',
        name: 'Default Brand',
        brand_id: 'brand_57a5dbca9f05f75a', // Existing testfinal brand
        industry: 'Technology'
      };
      
      setAvailableBrands([fallbackBrand]);
      if (!currentBrandId) {
        setBrandId(fallbackBrand.brand_id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setBrandId = (brandId: string) => {
    setCurrentBrandId(brandId);
    localStorage.setItem('selectedBrandId', brandId);
    localStorage.setItem('brand_id', brandId);
    sessionStorage.setItem('brand_id', brandId);
  };

  return (
    <BrandContext.Provider value={{
      currentBrandId,
      setBrandId,
      availableBrands,
      loadBrands,
      isLoading,
      error
    }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = (): BrandContextType => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

export default BrandContext;